import { db } from './db';
import { personnelMock } from '../mocks/personnel';
import { produitsMock } from '../mocks/stocks';
import { commandesMock, clientsMock } from '../mocks/commandes';
import { entrepriseSettingsMock } from '../mocks/entreprise';
import { typesHabitMock } from '../mocks/typesHabit';

export const API_URL = import.meta.env.VITE_API_URL || '/backend/api';

export const isOnline = () => window.navigator.onLine;
export const isDev = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || (import.meta.env.VITE_API_URL || '').includes('localhost');

const handleResponse = async (response: Response) => {
    const text = await response.text();
    try {
        const data = JSON.parse(text);
        if (response.status === 401) {
            console.warn("Session expirée ou invalide. Déconnexion automatique...");
            localStorage.removeItem('atelier_bassoum_token');
            localStorage.removeItem('atelier_bassoum_user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        if (!response.ok) {
            console.error("API Error Response:", data);
            throw new Error(data.message || data.error || 'Erreur API');
        }
        return data;
    } catch (err: any) {
        if (text.includes("<!DOCTYPE html>") || text.includes("<html>")) {
             console.error("HTML detected instead of JSON. Server Error Log excerpt:", text.substring(0, 200));
             throw new Error("Le serveur a renvoyé une page HTML au lieu de JSON. Vérifiez l'URL de l'API.");
        }
        console.error("Non-JSON Response or Parsing Error:", text);
        const excerpt = text.length > 100 ? text.substring(0, 100) + "..." : text;
        throw new Error(err.message || `Erreur Format Réponse (${response.status}): ${excerpt}`);
    }
};

// Queue helper
const queueAction = async (type: string, params: any) => {
  await db.actionsQueue.add({
    type,
    params,
    timestamp: Date.now()
  });
  console.log(`Action ${type} mise en file d'attente (Hors-ligne)`);
};

export const api = {
  // Generic GET
  get: async (endpoint: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const headers: any = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_URL}${endpoint}`, { headers });
    return await handleResponse(response);
  },
  getTodayVentes: async (token: string) => {
    const response = await fetch(`${API_URL}/ventes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Auth
  login: async (email: string, pin: string, user_id?: string | number) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, pin, user_id }),
    });
    return await handleResponse(response);
  },
  getPublicUsers: async () => {
    // Forcer le non-cache pour mobile et s'assurer que l'URL est propre
    const url = `${API_URL}/auth/users?t=${Date.now()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return await handleResponse(response);
  },

  // Commandes
  getCommandes: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/commandes`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      let data = await handleResponse(response);
      
      if (isDev() && data) {
        const localAffectations = await db.affectations.toArray();
        let personnelList = await db.personnel.toArray();
        if (personnelList.length === 0) personnelList = personnelMock as any;
        
        data = data.map((cmd: any) => {
          const cmdAffs = localAffectations.filter(a => Number(a.commande_id) === Number(cmd.id));
          if (cmdAffs.length > 0) {
            const enriched = cmdAffs.map(a => {
              const p = personnelList.find(px => String(px.id) === String(a.personnel_id));
              return { ...a, personnel_nom: p?.nom || p?.nom_complet || 'Inconnu' };
            });
            return { ...cmd, affectations: enriched };
          }
          return cmd;
        });
      }

      // Update cache atomically via transaction
      await db.transaction('rw', db.commandes, async () => {
          await db.commandes.clear();
          await db.commandes.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline() || isDev()) {
        console.log("Fallback: Récupération des commandes depuis le cache local.");
        const cached = await db.commandes.toArray();
        return cached.length > 0 ? cached : commandesMock;
      }
      throw err;
    }
  },

  getCommande: async (token: string, id: string) => {
    try {
        const response = await fetch(`${API_URL}/commandes/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await handleResponse(response);
        
        if (isDev() && data) {
          const localAffectations = await db.affectations
            .where('commande_id')
            .equals(Number(id))
            .toArray();
          
          if (localAffectations.length > 0) {
            const personnelList = await db.personnel.toArray();
            const enrichedLocal = localAffectations.map(a => {
              const p = personnelList.find(pers => String(pers.id) === String(a.personnel_id)) || 
                        personnelMock.find(pers => String(pers.id) === String(a.personnel_id));
              return {
                ...a,
                personnel_nom: p ? p.nom : "Personnel local",
                is_local: true
              };
            });
            data.affectations = [...(data.affectations || []), ...enrichedLocal];
          }
        }
        return data;
    } catch (err) {
        if (!isOnline() || isDev()) {
            const cached = await db.commandes.get(parseInt(id));
            if (isDev() && cached) {
                // Merge local affectations even for cached commande
                const localAffs = await db.affectations.where('commande_id').equals(Number(id)).toArray();
                const personnelList = await db.personnel.toArray();
                cached.affectations = [
                    ...(cached.affectations || []),
                    ...localAffs.map(a => ({
                        ...a,
                        personnel_nom: (personnelList.find(pers => String(pers.id) === String(a.personnel_id)) || 
                                       personnelMock.find(pers => String(pers.id) === String(a.personnel_id)))?.nom || "Personnel local"
                    }))
                ];
            }
            return cached;
        }
        throw err;
    }
  },

  createCommande: async (token: string, commandeData: any) => {
    if (!isOnline()) {
      await queueAction('createCommande', { token, commandeData });
      return { message: "Commande enregistrée localement (Hors-ligne)", offline: true };
    }
    const response = await fetch(`${API_URL}/commandes`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify(commandeData),
    });
    return await handleResponse(response);
  },
  
  updateCommande: async (token: string, id: string, data: any) => {
    const response = await fetch(`${API_URL}/commandes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  softDeleteCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  cancelCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/cancel`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashCommandes: async (token: string) => {
    const response = await fetch(`${API_URL}/commandes/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteCommande: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateCommandeStatus: async (token: string, id: string, statut: string, commentaire: string = "") => {
    const response = await fetch(`${API_URL}/commandes/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ statut, commentaire })
    });
    return await handleResponse(response);
  },
  
  revertCommandeStatus: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/commandes/${id}/revert`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  assignPersonnel: async (token: string, id: string, type: string, personnel_id: number) => {
    const response = await fetch(`${API_URL}/commandes/${id}/assign`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, personnel_id })
    });
    return await handleResponse(response);
  },

  updateCommandeTissu: async (token: string, id: string, tissuData: any) => {
    const response = await fetch(`${API_URL}/commandes/${id}/tissu`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(tissuData)
    });
    return await handleResponse(response);
  },

  getValidationHistory: async (token: string, id: string) => {
    const response = await fetch(`${API_URL}/validations?commande_id=${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getCommandePaiements: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/commandes/${id}/paiements`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  addCommandePaiement: async (token: string, id: string | number, data: { montant: number, mode_paiement: string, notes?: string }) => {
    if (!isOnline()) {
      await queueAction('addCommandePaiement', { token, id, data });
      return { message: "Paiement enregistré localement", offline: true };
    }
    const response = await fetch(`${API_URL}/commandes/${id}/paiements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Stocks
  getStocks: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/tissus?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
      });
      const data = await handleResponse(response);
      
      // Update cache atomically via transaction
      await db.transaction('rw', db.stocks, async () => {
          await db.stocks.clear();
          await db.stocks.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline() || isDev()) {
        const cached = await db.stocks.toArray();
        return cached.length > 0 ? cached : produitsMock;
      }
      throw err;
    }
  },

  createStock: async (token: string, stockData: any) => {
    if (!isOnline()) {
      await queueAction('createStock', { token, stockData });
      return { message: "Création stock enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/tissus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(stockData),
    });
    return await handleResponse(response);
  },

  updateStock: async (token: string, id: string, stockData: any) => {
    if (!isOnline()) {
      await queueAction('updateStock', { token, id, stockData });
      return { message: "Mise à jour stock enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/tissus/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(stockData),
    });
    return await handleResponse(response);
  },

  softDeleteStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashStocks: async (token: string) => {
    const response = await fetch(`${API_URL}/tissus/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteStock: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/tissus/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Clients
  getClients: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/clients`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleResponse(response);
      
      // Update cache atomically via transaction
      await db.transaction('rw', db.clients, async () => {
          await db.clients.clear();
          await db.clients.bulkPut(data);
      });
      
      return data;
    } catch (err) {
      if (!isOnline() || isDev()) {
        const cached = await db.clients.toArray();
        return cached.length > 0 ? cached : clientsMock;
      }
      throw err;
    }
  },

  createClient: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  getClientHistory: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/history`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  
  softDeleteClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getTrashClients: async (token: string) => {
    const response = await fetch(`${API_URL}/clients/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restoreClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeleteClient: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/clients/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Modèles
  getModeles: async (token: string) => {
    const response = await fetch(`${API_URL}/modeles`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  createModele: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/modeles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  updateModele: async (token: string, id: string, data: any) => {
    const response = await fetch(`${API_URL}/modeles/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Ventes
  getVentesToday: async (token: string) => {
    const response = await fetch(`${API_URL}/ventes`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  
  // Personnel
  getPersonnel: async (token: string) => {
    const mapPersonnel = (data: any[]) => data.map((p: any) => ({
      ...p,
      id: p.id ? String(p.id) : `temp_${Math.random()}`,
      tarif_par_coupe: p.tarif_par_coupe ?? p.tarifParCoupe ?? 0,
      tarif_par_couture: p.tarif_par_couture ?? p.tarifParCouture ?? 0,
      tarif_par_broderie: p.tarif_par_broderie ?? p.tarifParBroderie ?? 0,
      tarifs_json: p.tarifs_json ?? p.tarifsParType ?? [],
      commandes_mois: p.commandes_mois ?? p.commandesMois ?? 0,
      commandes_total: p.commandes_total ?? p.commandesTotal ?? 0,
      gains_mois: p.gains_mois ?? p.gainsMois ?? 0,
      actif: p.actif !== undefined ? (p.actif ? 1 : 0) : 1
    }));

    try {
      const response = await fetch(`${API_URL}/personnel`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await handleResponse(response);
      
      // Update cache ONLY if we have data from server
      if (Array.isArray(data) && data.length > 0) {
        console.log("getPersonnel: Mise à jour du cache local avec", data.length, "éléments.");
        await db.transaction('rw', db.personnel, async () => {
            await db.personnel.clear();
            await db.personnel.bulkPut(data);
        });
      }
      
      // If server returned empty but we are in dev mode, return local cache + mocks
      if (Array.isArray(data) && data.length === 0 && isDev()) {
         const cached = await db.personnel.toArray();
         console.log("getPersonnel: Serveur vide, chargement du cache local (", cached.length, "éléments) + mocks.");
         // Fusionner le cache local (nouveaux ajouts) avec les mocks
         const combined = [...cached];
         personnelMock.forEach(pm => {
           if (!combined.find(c => c.id === pm.id || c.nom === pm.nom)) {
             combined.push(pm);
           }
         });
         return mapPersonnel(combined);
      }

      console.log("getPersonnel: Retour des données serveur (", data.length, "éléments).");
      return Array.isArray(data) ? mapPersonnel(data) : [];
    } catch (err) {
      if (!isOnline() || isDev()) {
        console.log("Fallback: Récupération du personnel depuis le cache local.");
        const cached = await db.personnel.toArray();
        const combined = [...cached];
        personnelMock.forEach(pm => {
           if (!combined.find(c => c.id === pm.id || c.nom === pm.nom)) {
             combined.push(pm);
           }
        });
        return mapPersonnel(combined);
      }
      throw err;
    }
  },

  // Rapports
  getGlobalStats: async (token: string) => {
    const response = await fetch(`${API_URL}/rapports/stats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  getCommandesReport: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/rapports/commandes?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  getVentesReport: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/rapports/ventes?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  // Utilisateurs (User Management)
  getUsers: async (token: string) => {
    const response = await fetch(`${API_URL}/utilisateurs`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },
  createUser: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/utilisateurs`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  updateUser: async (token: string, id: string | number, data: any) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getTrashUsers: async (token: string) => {
    const response = await fetch(`${API_URL}/utilisateurs/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  restoreUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  hardDeleteUser: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/utilisateurs/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getPaieStats: async (token: string, debut: string, fin: string) => {
    const response = await fetch(`${API_URL}/paie?debut=${debut}&fin=${fin}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  getPaieHistory: async (token: string) => {
    const response = await fetch(`${API_URL}/paie/historique`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  savePaieRecord: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/paie`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deletePaieRecord: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/paie/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  createVente: async (token: string, data: any) => {
    if (!isOnline()) {
      await queueAction('createVente', { token, venteData: data });
      return { message: "Vente enregistrée localement", offline: true };
    }
    const response = await fetch(`${API_URL}/ventes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  updateVentePaiement: async (token: string, id: number, data: any) => {
    const response = await fetch(`${API_URL}/ventes/${id}/paiement`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  cancelVente: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/ventes/${id}/cancel`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateVente: async (token: string, id: number, data: any) => {
    const response = await fetch(`${API_URL}/ventes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  uploadFacturePdf: async (token: string, blob: Blob, filename: string) => {
    const formData = new FormData();
    formData.append('pdf', blob, filename);
    const response = await fetch(`${API_URL}/upload-pdf`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return await handleResponse(response);
  },

  // --- UPLOADS ---
  uploadImage: async (token: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    return await handleResponse(response);
  },

  // --- MESURES ---
  getMesureChamps: async (token: string) => {
    const response = await fetch(`${API_URL}/mesures/champs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getClientProfils: async (token: string) => {
    const response = await fetch(`${API_URL}/mesures/profils`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  saveClientMesures: async (token: string, client_id: number, mesures: any[], notes: string = "") => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ mesures, notes }),
    });
    return await handleResponse(response);
  },

  getClientMesures: async (token: string, client_id: number) => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  deleteClientMesures: async (token: string, client_id: number) => {
    const response = await fetch(`${API_URL}/mesures/${client_id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await handleResponse(response);
  },

  // Settings
  getSettings: async (token: string) => {
    const response = await fetch(`${API_URL}/settings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  updateSettings: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Finance & Trésorerie
  getFinances: async (token: string, filters: any = {}) => {
    const params = new URLSearchParams(filters).toString();
    const response = await fetch(`${API_URL}/finances?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getFinanceStats: async (token: string) => {
    const response = await fetch(`${API_URL}/finances/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getFinanceCategories: async (token: string) => {
    const response = await fetch(`${API_URL}/finances/categories`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  addFinance: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/finances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteFinance: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/finances/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Personnel Management
  updatePersonnel: async (token: string, id: string | number, data: any) => {
    try {
      const response = await fetch(`${API_URL}/personnel/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      const result = await handleResponse(response);
      
      if (isDev()) {
         await db.personnel.update(String(id), {
           ...data,
           id: String(id),
           // Map snake_case to camelCase for the mock-like local DB if needed
           tarifParCoupe: data.tarif_par_coupe,
           tarifParCouture: data.tarif_par_couture,
           tarifParBroderie: data.tarif_par_broderie,
           tarifsParType: data.tarifs_json
         });
      }
      return result;
    } catch (err) {
      if (isDev()) {
        await db.personnel.update(String(id), {
           ...data,
           id: String(id),
           tarifParCoupe: data.tarif_par_coupe,
           tarifParCouture: data.tarif_par_couture,
           tarifParBroderie: data.tarif_par_broderie,
           tarifsParType: data.tarifs_json
        });
        return { message: "Mis à jour localement (Mode Dev)", offline: true };
      }
      throw err;
    }
  },
  
  softDeletePersonnel: async (token: string, id: string | number) => {
    try {
      const response = await fetch(`${API_URL}/personnel/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await handleResponse(response);
      if (API_URL.includes('localhost')) {
          await db.personnel.delete(String(id));
      }
      return result;
    } catch (err) {
      if (API_URL.includes('localhost')) {
        await db.personnel.delete(String(id));
        return { message: "Supprimé localement (Mode Dev)", offline: true };
      }
      throw err;
    }
  },

  getTrashPersonnel: async (token: string) => {
    const response = await fetch(`${API_URL}/personnel/trash`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getPersonnelHistory: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/personnel/${id}/history`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  restorePersonnel: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/personnel/${id}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  hardDeletePersonnel: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/personnel/${id}/hard`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createPersonnel: async (token: string, data: any) => {
    try {
      const response = await fetch(`${API_URL}/personnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      const result = await handleResponse(response);
      
      if (isDev()) {
        console.log("createPersonnel: Mode Dev détecté, ajout local de", data.nom);
        await db.personnel.add({
          ...data,
          id: result.id || `temp_${Date.now()}`,
          commandes_mois: 0,
          commandes_total: 0,
          gains_mois: 0
        });
      }
      
      return result;
    } catch (err) {
      if (API_URL.includes('localhost')) {
        const tempId = `temp_${Date.now()}`;
        const newPers = {
          ...data,
          id: tempId,
          commandes_mois: 0,
          commandes_total: 0,
          gains_mois: 0
        };
        await db.personnel.add(newPers);
        return { id: tempId, message: "Personnel ajouté localement (Mode Dev)", offline: true };
      }
      throw err;
    }
  },

  // Mesure Champs Management
  saveMesureChamp: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/mesures/champs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteMesureChamp: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/mesures/champs?id=${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  saveMesureChampsOrder: async (token: string, data: {id: number, ordre: number}[]) => {
    const response = await fetch(`${API_URL}/mesures/champs/order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  // Habit Types Management
  getHabitTypes: async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/habits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return await handleResponse(response);
    } catch (err) {
      if (!isOnline() || API_URL.includes('localhost')) {
        return typesHabitMock;
      }
      throw err;
    }
  },
  saveHabitType: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/habits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  deleteHabitType: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/habits/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Affectations granulaires
  getAffectations: async (token: string, commandeId: string | number) => {
    try {
      const response = await fetch(`${API_URL}/commandes/${commandeId}/affectations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await handleResponse(response);
      
      if (isDev()) {
        const localAffectations = await db.affectations
          .where('commande_id')
          .equals(Number(commandeId))
          .toArray();
        
        // Enrich local affectations with personnel names if possible
        const personnelList = await db.personnel.toArray();
        const enrichedLocal = localAffectations.map(a => {
          const p = personnelList.find(pers => String(pers.id) === String(a.personnel_id)) || 
                    personnelMock.find(pers => String(pers.id) === String(a.personnel_id));
          return {
            ...a,
            personnel_nom: p ? p.nom : "Personnel inconnu",
            is_local: true
          };
        });

        return [...(Array.isArray(data) ? data : []), ...enrichedLocal];
      }

      return data;
    } catch (err) {
      if (isDev()) {
        const localAffectations = await db.affectations
          .where('commande_id')
          .equals(Number(commandeId))
          .toArray();
        
        const personnelList = await db.personnel.toArray();
        return localAffectations.map(a => {
          const p = personnelList.find(pers => String(pers.id) === String(a.personnel_id)) || 
                    personnelMock.find(pers => String(pers.id) === String(a.personnel_id));
          return {
            ...a,
            personnel_nom: p ? p.nom : "Personnel local",
            is_local: true
          };
        });
      }
      throw err;
    }
  },

  createAffectation: async (token: string, commandeId: string | number, data: { personnel_id: number | string, type: string, tache: string, statut?: string, cout_main_doeuvre?: number, article_id?: number | null, quantite?: number }) => {
    const isMockId = typeof data.personnel_id === 'string' && (data.personnel_id.startsWith('pt') || data.personnel_id.startsWith('temp_'));
    
    if (isMockId && isDev()) {
      console.log("createAffectation: Enregistrement local pour ID mock", data.personnel_id);
      const id = await db.affectations.add({
        ...data,
        commande_id: Number(commandeId),
        date_affectation: new Date().toISOString(),
        statut: data.statut || 'EN_ATTENTE'
      });
      return { id, message: "Affectation locale créée", offline: true };
    }

    try {
      const response = await fetch(`${API_URL}/commandes/${commandeId}/affectations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await handleResponse(response);
    } catch (err) {
      if (isDev()) {
        const id = await db.affectations.add({
          ...data,
          commande_id: Number(commandeId),
          date_affectation: new Date().toISOString(),
          statut: data.statut || 'EN_ATTENTE'
        });
        return { id, message: "Affectation locale créée (Fallback)", offline: true };
      }
      throw err;
    }
  },

  updateAffectationStatus: async (token: string, id: number, statut: string, quantiteTerminee?: number) => {
    try {
      const response = await fetch(`${API_URL}/affectations/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ statut, quantite_terminee: quantiteTerminee })
      });
      const result = await handleResponse(response);
      if (isDev()) {
        const localAff = await db.affectations.get(id);
        if (localAff) {
          const originalQty = Number(localAff.quantite || 1);
          if (quantiteTerminee && quantiteTerminee > 0 && quantiteTerminee < originalQty) {
            const originalCost = Number(localAff.cout_main_doeuvre || 0);
            const costDone = Math.round((originalCost / originalQty) * quantiteTerminee);
            const costRemaining = originalCost - costDone;

            await db.affectations.update(id, {
              quantite: quantiteTerminee,
              cout_main_doeuvre: costDone,
              statut
            });

            await db.affectations.add({
              commande_id: localAff.commande_id,
              personnel_id: localAff.personnel_id,
              type: localAff.type,
              tache: localAff.tache,
              cout_main_doeuvre: costRemaining,
              statut: 'EN_ATTENTE',
              article_id: localAff.article_id,
              quantite: originalQty - quantiteTerminee
            });
          } else {
            await db.affectations.update(id, { statut });
          }
        }
      }
      return result;
    } catch (err) {
      if (isDev()) {
        const localAff = await db.affectations.get(id);
        if (localAff) {
          const originalQty = Number(localAff.quantite || 1);
          if (quantiteTerminee && quantiteTerminee > 0 && quantiteTerminee < originalQty) {
            const originalCost = Number(localAff.cout_main_doeuvre || 0);
            const costDone = Math.round((originalCost / originalQty) * quantiteTerminee);
            const costRemaining = originalCost - costDone;

            await db.affectations.update(id, {
              quantite: quantiteTerminee,
              cout_main_doeuvre: costDone,
              statut
            });

            await db.affectations.add({
              commande_id: localAff.commande_id,
              personnel_id: localAff.personnel_id,
              type: localAff.type,
              tache: localAff.tache,
              cout_main_doeuvre: costRemaining,
              statut: 'EN_ATTENTE',
              article_id: localAff.article_id,
              quantite: originalQty - quantiteTerminee
            });
          } else {
            await db.affectations.update(id, { statut });
          }
        }
        return { message: "Statut mis à jour localement", offline: true };
      }
      throw err;
    }
  },

  deleteAffectation: async (token: string, id: number) => {
    try {
      const response = await fetch(`${API_URL}/affectations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await handleResponse(response);
      if (isDev()) {
        await db.affectations.delete(id);
      }
      return result;
    } catch (err) {
      if (isDev()) {
        await db.affectations.delete(id);
        return { message: "Supprimé localement", offline: true };
      }
      throw err;
    }
  },

  // Helper for CORS-friendly image URLs
  getProxyUrl: (url: string | null | undefined) => {
    if (!url) return '';
    // Extraire seulement le nom du fichier, qu'il provienne d'une URL absolue, relative ou juste le nom brut
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return `${API_URL}/proxy/${filename}`;
  },

  // Fournisseurs
  getFournisseurs: async (token: string) => {
    const response = await fetch(`${API_URL}/fournisseurs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createFournisseur: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  getFournisseurLots: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  createFournisseurLot: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  getLotDetails: async (token: string, lotId: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  addLotArticle: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}/articles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  addLotPaiement: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}/paiements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },
  updateArticleStock: async (token: string, articleId: string | number, decrement: number) => {
    const response = await fetch(`${API_URL}/articles/${articleId}/update-stock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ decrement })
    });
    return await handleResponse(response);
  },
  updateLotStatus: async (token: string, lotId: string | number, statut: string) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ statut })
    });
    return await handleResponse(response);
  },
  deleteFournisseur: async (token: string, id: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateLot: async (token: string, lotId: string | number, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/lots/${lotId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deleteLot: async (token: string, supplierId: string | number, lotId: string | number) => {
    const response = await fetch(`${API_URL}/fournisseurs/${supplierId}/${lotId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  getPaymentModes: async (token: string) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  createPaymentMode: async (token: string, data: any) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await handleResponse(response);
  },

  deletePaymentMode: async (token: string, id: number) => {
    const response = await fetch(`${API_URL}/fournisseurs/modes-paiement/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  // Permissions
  getPermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  getUserPermissions: async (token: string, userId: string | number) => {
    const response = await fetch(`${API_URL}/permissions/user/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },
  updateUserPermissions: async (token: string, userId: string | number, permissions: number[]) => {
    const response = await fetch(`${API_URL}/permissions/assign/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ permissions })
    });
    return await handleResponse(response);
  },

  getRolePermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions/matrix`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  updateRolePermissions: async (token: string, matrix: any) => {
    const response = await fetch(`${API_URL}/permissions/matrix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ matrix })
    });
    return await handleResponse(response);
  },

  getMyPermissions: async (token: string) => {
    const response = await fetch(`${API_URL}/permissions/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  backupDatabase: async (token: string) => {
    const response = await fetch(`${API_URL}/backup_database.php`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Échec du backup');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_atelier_bassoum_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  resetDatabase: async (token: string) => {
    const response = await fetch(`${API_URL}/reset_database.php`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await handleResponse(response);
  },

  API_URL, // Expose for components that need it for manual fetch
};
